import { useTemporaryStore } from '@/contexts/temporaryStore'
import { cn } from '@/lib/utils'
import { useEffect, useLayoutEffect, useState } from 'react'
import { createPortal } from 'react-dom'

const ANIMATION_DURATION = 500

interface RenderedBlock {
  text: string
  isExiting: boolean
}

export default function ProcessingIndicator() {
  const activeProcessingBlocks = useTemporaryStore(s => s.activeProcessingBlocks)
  const [renderedBlocks, setRenderedBlocks] = useState<Record<string, RenderedBlock>>({})

  useEffect(() => {
    setRenderedBlocks(prev => {
      const next = { ...prev }

      // Mark removed blocks as exiting
      Object.keys(next).forEach(id => {
        if (!activeProcessingBlocks[id]) {
          next[id] = { ...next[id], isExiting: true }
        }
      })

      // Add or update active blocks
      Object.entries(activeProcessingBlocks).forEach(([id, text]) => {
        next[id] = { text, isExiting: false }
      })

      return next
    })
  }, [activeProcessingBlocks])

  const handleExited = (id: string) => {
    setRenderedBlocks(prev => {
      const next = { ...prev }
      delete next[id]
      return next
    })
  }

  return (
    <>
      {Object.entries(renderedBlocks).map(([blockId, { text, isExiting }]) => (
        <BlockIndicator
          key={blockId}
          blockId={blockId}
          text={text}
          isExiting={isExiting}
          onExited={() => handleExited(blockId)}
        />
      ))}
    </>
  )
}

const BlockIndicator = ({
  blockId,
  text,
  isExiting,
  onExited,
}: {
  blockId: string
  text: string
  isExiting: boolean
  onExited: () => void
}) => {
  const [position, setPosition] = useState<{ top: number; left: number } | null>(null)
  const [visible, setVisible] = useState(false)

  useLayoutEffect(() => {
    let animationFrameId: number

    const updatePosition = () => {
      const element = document.querySelector(`[data-block-id="${blockId}"]`)
      if (element) {
        const rect = element.getBoundingClientRect()
        setPosition({
          top: rect.bottom,
          left: rect.left,
        })
      } else {
        setPosition(null)
      }
    }

    updatePosition()
    // Trigger fade-in
    requestAnimationFrame(() => setVisible(true))

    const loop = () => {
      updatePosition()
      animationFrameId = requestAnimationFrame(loop)
    }
    loop()

    return () => {
      cancelAnimationFrame(animationFrameId)
    }
  }, [blockId])

  useEffect(() => {
    if (isExiting) {
      setVisible(false)
      const timer = setTimeout(onExited, ANIMATION_DURATION)
      return () => clearTimeout(timer)
    }
  }, [isExiting, onExited])

  if (!position) return null

  return createPortal(
    <div
      // className={`fixed z-50 transition-opacity duration-300 ${visible ? 'opacity-100' : 'opacity-0'}`}
      className={cn(
        'pointer-events-none fixed z-[1] translate-y-[0.5rem] opacity-0 transition-opacity duration-300',
        visible && 'opacity-100'
      )}
      style={{
        left: position.left,
        top: position.top,
      }}
    >
      <div className="relative rounded-md bg-white px-4 py-2 shadow-[0_0_10px_0_rgba(0,0,0,0.1)]">
        <div className="flex items-center gap-2">
          <div className="h-4 w-4 animate-spin rounded-full border-2 border-[#9065B0] border-t-transparent"></div>
          <span className="text-sm font-normal text-black">{text}</span>
        </div>
      </div>
    </div>,
    document.body
  )
}
